import { enUS } from "./messages/en-US";
import { deDE } from "./messages/de-DE";
import { zhCN } from "./messages/zh-CN";
import {
  applyDocumentLocale,
  DEFAULT_LOCALE,
  readStoredLocale,
  resolveLocale,
  writeStoredLocale,
} from "./resolveLocale";
import { translate } from "./translate";
import type { AppLocale, MessageTree, TranslateVars } from "./types";

export const CATALOGS: Record<AppLocale, MessageTree> = {
  "zh-CN": zhCN,
  "en-US": enUS,
  "de-DE": deDE,
};

/**
 * 进程内当前语言。React 通过 `useSyncExternalStore` 订阅，
 * 非 React 工具（`utils/format`、`utils/statusLabels`）直接读取，
 * 保证同一时刻组件与工具函数的语言一致。
 */
let currentLocale: AppLocale = DEFAULT_LOCALE;
let initialized = false;
const listeners = new Set<() => void>();

export function getRuntimeLocale(): AppLocale {
  return currentLocale;
}

export function subscribeRuntimeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function setRuntimeLocale(locale: AppLocale): void {
  if (locale === currentLocale) return;
  currentLocale = locale;
  for (const listener of Array.from(listeners)) listener();
}

/** 解析并应用启动语言：已保存的选择 > 浏览器语言 > zh-CN。 */
export function initRuntimeLocale(options?: { force?: boolean }): AppLocale {
  if (initialized && !options?.force) return currentLocale;
  initialized = true;
  const resolved = resolveLocale({ stored: readStoredLocale() });
  setRuntimeLocale(resolved);
  if (typeof document !== "undefined") applyDocumentLocale(resolved);
  return resolved;
}

/** 切换语言：立即生效、写入 localStorage、同步 `<html lang>`。 */
export function changeRuntimeLocale(locale: AppLocale): void {
  initialized = true;
  setRuntimeLocale(locale);
  writeStoredLocale(locale);
  if (typeof document !== "undefined") applyDocumentLocale(locale);
}

/** 测试用：把运行时语言恢复到默认值并清空初始化标记。 */
export function resetRuntimeLocale(locale: AppLocale = DEFAULT_LOCALE): void {
  initialized = false;
  setRuntimeLocale(locale);
}

export function catalogFor(locale: AppLocale): MessageTree {
  return CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
}

/** 当前语言下的翻译函数，供非 React 模块使用。 */
export function t(key: string, vars?: TranslateVars): string {
  return translate(catalogFor(currentLocale), key, vars);
}
